export const MAINTENANCE_SCOPES = Object.freeze({
  ACTIVE: 'ACTIVE',
  FULL: 'FULL',
});

const optionalNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const validDateTime = (value) => {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const calculateMedian = (values) => {
  const sorted = values.filter(Number.isFinite).sort((left, right) => left - right);
  if (!sorted.length) return null;
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

export const calculateFuelCostPerKm = (readings = []) => {
  const sortedReadings = [...readings].sort((left, right) => (
    (validDateTime(left.Date) || 0) - (validDateTime(right.Date) || 0)
  ));
  const costsPerKm = [];
  for (let index = 1; index < sortedReadings.length; index += 1) {
    const previousKm = optionalNumber(sortedReadings[index - 1].Kilometre);
    const currentKm = optionalNumber(sortedReadings[index].Kilometre);
    const currentCost = optionalNumber(sortedReadings[index].PrixTotal);
    if (previousKm === null || currentKm === null || currentCost === null) continue;
    const distance = currentKm - previousKm;
    if (distance > 0 && currentCost >= 0) costsPerKm.push(currentCost / distance);
  }
  const costPerKm = calculateMedian(costsPerKm);
  return costPerKm === null
    ? { status: 'INSUFFICIENT', reason: 'Au moins deux relevés kilométriques cohérents sont nécessaires.' }
    : { status: 'OK', costPerKm, intervalCount: costsPerKm.length };
};

export const calculateMaintenanceCostPerKm = (vehicle, scope = MAINTENANCE_SCOPES.ACTIVE) => {
  const lastReading = vehicle?.DernierReleve;
  const endKm = optionalNumber(lastReading?.Kilometre);
  const endDate = validDateTime(lastReading?.Date);
  if (endKm === null || endDate === null) {
    return { status: 'INSUFFICIENT', reason: 'Le dernier relevé kilométrique est manquant.' };
  }

  const allMaintenances = [...(vehicle?.EntretiensRealises || [])]
    .filter((item) => {
      const date = validDateTime(item.Date);
      return date !== null && date <= endDate;
    })
    .sort((left, right) => validDateTime(left.Date) - validDateTime(right.Date));

  let startKm;
  let startDate;
  let startSource = 'PREMIER_RELEVE';
  let maintenances;
  if (scope === MAINTENANCE_SCOPES.FULL) {
    const oldestMaintenance = allMaintenances[0];
    const firstReading = vehicle?.PremierReleve;
    const startCandidates = [
      {
        source: 'PREMIER_RELEVE',
        kilometre: optionalNumber(firstReading?.Kilometre),
        date: validDateTime(firstReading?.Date),
      },
      {
        source: 'PLUS_ANCIEN_ENTRETIEN',
        kilometre: optionalNumber(oldestMaintenance?.Kilometre),
        date: validDateTime(oldestMaintenance?.Date),
      },
    ]
      .filter((candidate) => candidate.kilometre !== null && candidate.date !== null)
      .sort((left, right) => left.kilometre - right.kilometre || left.date - right.date);
    const oldestAvailableBoundary = startCandidates[0];
    startKm = oldestAvailableBoundary?.kilometre ?? null;
    startDate = oldestAvailableBoundary?.date ?? null;
    if (startKm === null || startDate === null) {
      return {
        status: 'INSUFFICIENT',
        reason: 'Le premier relevé ou le plus ancien entretien doit posséder un kilométrage.',
      };
    }
    maintenances = allMaintenances;
    startSource = oldestAvailableBoundary.source;
  } else {
    const firstReading = vehicle?.PremierReleve;
    startKm = optionalNumber(firstReading?.Kilometre);
    startDate = validDateTime(firstReading?.Date);
    if (startKm === null || startDate === null) {
      return { status: 'INSUFFICIENT', reason: 'Le premier relevé kilométrique est manquant.' };
    }
    maintenances = allMaintenances.filter((item) => {
      const date = validDateTime(item.Date);
      return !item.EstArchive && date >= startDate;
    });
  }

  const distanceKm = endKm - startKm;
  if (distanceKm <= 0) {
    return { status: 'INSUFFICIENT', reason: 'La distance observée doit être supérieure à zéro.' };
  }
  const pricedMaintenances = maintenances.filter((item) => {
    const cost = optionalNumber(item.Cout);
    return cost !== null && cost >= 0;
  });
  if (!pricedMaintenances.length) {
    return { status: 'INSUFFICIENT', reason: 'Aucun entretien réalisé avec un coût exploitable.' };
  }
  const observedCost = pricedMaintenances.reduce((sum, item) => sum + optionalNumber(item.Cout), 0);
  return {
    status: 'OK',
    costPerKm: observedCost / distanceKm,
    observedCost,
    distanceKm,
    maintenanceCount: pricedMaintenances.length,
    startKm,
    endKm,
    startSource,
  };
};

export const calculateVehicleSimulation = ({ vehicle, distanceKm, maintenanceScope }) => {
  const simulatedDistance = optionalNumber(distanceKm);
  const fuel = calculateFuelCostPerKm(vehicle?.RelevesRecents || []);
  const maintenance = calculateMaintenanceCostPerKm(vehicle, maintenanceScope);
  const fuelTotal = fuel.status === 'OK' && simulatedDistance > 0
    ? fuel.costPerKm * simulatedDistance
    : null;
  const maintenanceTotal = maintenance.status === 'OK' && simulatedDistance > 0
    ? maintenance.costPerKm * simulatedDistance
    : null;
  return {
    vehicle,
    fuel,
    maintenance,
    fuelTotal,
    maintenanceTotal,
    total: fuelTotal !== null && maintenanceTotal !== null ? fuelTotal + maintenanceTotal : null,
    totalPerKm: fuel.status === 'OK' && maintenance.status === 'OK'
      ? fuel.costPerKm + maintenance.costPerKm
      : null,
  };
};
